import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MtnaLogger } from '@mtna/core-angular';
import { Snippet, SnippetGenerator } from '@rds/rds-packaging-ts';
import {
  RdsApiService,
  RdsApiSnippetQueryType,
  RdsRawApiQuery,
  SelectParameters,
  serializeBaseUrlParam,
  serializeSelectOptionsUrlParams,
  serializeTabulationOptionsUrlParams,
  TabulationParameters,
} from '@rds/shared-api';
import { Observable, of, ReplaySubject } from 'rxjs';
import { concatMap, map, tap } from 'rxjs/operators';

import { RdsServerService } from './rds-server.service';

@Injectable({ providedIn: 'root' })
export class RdsSnippetService {
  get API_URL(): string {
    let apiUrl: string = this.apiService.getApiUrl() || '';
    if (!apiUrl?.endsWith('/app-api')) {
      // Support backwards compatibility
      // The API url should no longer end with '/api',
      // but point to the root of the rds instance.
      apiUrl += '/app-api';
    }
    return `${apiUrl}`;
  }

  /** base url for the snippet service */
  get SNIPPET_URL(): string {
    return `${this.API_URL}/snippet`;
  }

  /** url for the selected data product */
  protected get DATA_PRODUCT_URL(): string {
    return `${this.SNIPPET_URL}/${this.apiService.getCatalogId()}/${this.apiService.getDataProductId()}`;
  }

  protected _snippetGeneratorsCache$ = new ReplaySubject<SnippetGenerator[]>(1);
  protected _snippetGeneratorsRequest$: Observable<SnippetGenerator[]> | undefined;
  protected _selectCache = new Map<string, Snippet>();
  protected _tabulateCache = new Map<string, Snippet>();

  constructor(
    protected apiService: RdsApiService,
    private http: HttpClient,
    protected logger: MtnaLogger,
    private sanitizer: DomSanitizer,
    private serverService: RdsServerService
  ) {}

  /**
   * Snippet generators can be used to create calls in external libraries that
   * when run, will get the data set represented by a provided query. This end
   * point exposes the available snippet generators and their details.
   * @remarks This method uses caching, returns a hot observable.
   * @param refresh forces a cache refresh, will update all subscribers. Defaults to false.
   * @returns all available snippet generators
   */
  getSnippetGenerators(refresh: boolean = false): Observable<SnippetGenerator[]> {
    if (refresh || !this._snippetGeneratorsRequest$) {
      this.logger.debug(`[RdsSnippetService] getSnippetGenerators - Fetching, ${refresh ? 'forced refresh' : 'not in cache'}`);
      this._snippetGeneratorsRequest$ = this.http.get<SnippetGenerator[]>(`${this.SNIPPET_URL}/generators`);
      this._snippetGeneratorsRequest$.subscribe(
        (result) => this._snippetGeneratorsCache$.next(result),
        (err) => this._snippetGeneratorsCache$.error(err)
      );
    }
    return this._snippetGeneratorsCache$.asObservable();
  }

  /**
   * Requests a snippet of code in the requested library to recreate the
   * provided select query.
   *
   * @remarks This method uses caching, returns a cold observable.
   * @param params various select parameters
   * @param library the name of the library for the requested code snippet
   * @param version the version of the library for the requested code snippet
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns The code snippet for the specified select query applicable to the given version of the library
   */
  generateSelectSnippet(params: SelectParameters, library: string, version: string, refresh: boolean = false): Observable<Snippet> {
    return this.serverService.getBasePath().pipe(
      map((basePath) => {
        const libraryParam = serializeBaseUrlParam('library', library);
        const versionParam = serializeBaseUrlParam('version', version);
        const baseUrlParam = serializeBaseUrlParam('baseUrl', basePath);
        const selectParams = serializeSelectOptionsUrlParams(params);
        // Full API path
        return `${this.DATA_PRODUCT_URL}/select?` + libraryParam + versionParam + baseUrlParam + selectParams;
      }),
      concatMap((path) => {
        const snippet = this._selectCache.get(path);
        if (refresh || !snippet) {
          this.logger.debug(`[RdsSnippetService] generateSelectSnippet - Fetching from API ${refresh ? '(refresh)' : ''}- ${path}`);
          return this.http.get<Snippet>(path).pipe(tap((ds) => this._selectCache.set(path, ds)));
        }
        this.logger.debug(`[RdsSnippetService] generateSelectSnippet - Returning from cache - ${path}`);
        return of(snippet);
      })
    );
  }

  /**
   * Requests a snippet of code in the requested library to recreate the
   * provided tabulate query.
   *
   * @remarks This method uses caching, returns a cold observable.
   * @param params various tabulation parameters
   * @param library the name of the library for the requested code snippet
   * @param version the version of the library for the requested code snippet
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns The code snippet for the specified tabulate query applicable to the given version of the library
   */
  generateTabulateSnippet(params: TabulationParameters, library: string, version: string, refresh: boolean = false): Observable<Snippet> {
    return this.serverService.getBasePath().pipe(
      map((basePath) => {
        const libraryParam = serializeBaseUrlParam('library', library);
        const versionParam = serializeBaseUrlParam('version', version);
        const baseUrlParam = serializeBaseUrlParam('baseUrl', basePath);
        const tabulateParams = serializeTabulationOptionsUrlParams(params);
        // Full API path
        return `${this.DATA_PRODUCT_URL}/tabulate?` + libraryParam + versionParam + baseUrlParam + tabulateParams;
      }),
      concatMap((path) => {
        const snippet = this._tabulateCache.get(path);
        if (refresh || !snippet) {
          this.logger.debug(`[RdsSnippetService] generateTabulateSnippet - Fetching from API ${refresh ? '(refresh)' : ''}- ${path}`);
          return this.http.get<Snippet>(path).pipe(tap((ds) => this._tabulateCache.set(path, ds)));
        }
        this.logger.debug(`[RdsSnippetService] generateTabulateSnippet - Returning from cache - ${path}`);
        return of(snippet);
      })
    );
  }

  /**
   * Create the html representation and raw query path.
   *
   * @param type the type of query, SELECT or TABULATE
   * @param params the query parameters
   * @returns an html representation and the raw RDS API path
   */
  generateRawApiQuery(type: RdsApiSnippetQueryType, params: SelectParameters | TabulationParameters): Observable<RdsRawApiQuery> {
    return this.serverService.getBasePath().pipe(
      map((base) => {
        const basePath = base + (base.endsWith('/') ? 'api' : '/api');

        // Base url and query type (select/tabulate)
        const baseUrl = `${basePath}/query/${this.apiService.getCatalogId()}/${this.apiService.getDataProductId()}/${type.toLowerCase()}?`;
        // Query-type specific query parameters
        const queryParams =
          type === 'SELECT' ? serializeSelectOptionsUrlParams(params) : serializeTabulationOptionsUrlParams(params as TabulationParameters);

        // Generate html
        const space = 8;
        let html = '';
        html += `${basePath}/query/` + '<br />';
        html += `<span style="display:inline-block; width: ${space * 2}px"></span>${this.apiService.getCatalogId()}/` + '<br />';
        html += `<span style="display:inline-block; width: ${space * 2}px"></span>${this.apiService.getDataProductId()}/` + '<br />';
        html += `<span style="display:inline-block; width: ${space * 2}px"></span>${type.toLowerCase()}`;

        // Split them on the ampersand to get individual
        if (queryParams) {
          const individualQueryParams: string[] = queryParams.split('&');
          if (individualQueryParams.length) {
            html += `<span class="rds-c-snippet-secondary-color" style="margin-left:${space}px;">?</span>` + '<br />';
          }

          // Render the parameters
          // - Display as a table to keep parameters aligned
          html += `<table style="margin-left:${space * 4}px; border: none; border-spacing: unset; border-collapse: collapse;">`;
          for (let i = 0; i < individualQueryParams.length; i++) {
            // The serialize method encodes the parameters, so we need to decode here
            const decoded: string = decodeURIComponent(individualQueryParams[i]);
            const nameAndParam = decoded
              // Split on the first occurance of the equal sign
              .split(/=(.+)/)
              // Filter out empty strings
              .filter((s) => !!s);
            if (nameAndParam.length === 2) {
              html += '<tr>';
              html += `<td><em>${nameAndParam[0]}</em></td>`;
              html += `<td style="padding: 0 ${space}px">=</td>`;
              html += `<td>${nameAndParam[1]}`;
              if (i < individualQueryParams.length - 1) {
                // If there is another parameter, add the ampersand
                html += `<span class="rds-c-snippet-secondary-color" style="margin-left:${space}px;">&</span>`;
              }
              html += '</td></tr>';
            }
          }
          html += '</table>';
        }
        return new RdsRawApiQuery(this.sanitizer.bypassSecurityTrustHtml(html), `${baseUrl}${queryParams}`);
      })
    );
  }
}
